/**
 * useInstallPrompt (Phase 16A, design §4): which install path this browser offers.
 *
 * - 'prompt':        Chromium (Android phone/tablet): native dialog via beforeinstallprompt.
 * - 'ios':           iPhone/iPad Safari: Share → Add to Home Screen instructions.
 * - 'firefox':       Firefox Android: menu → Install instructions.
 * - 'in-app':        embedded browser: open in Chrome/Safari to install.
 * - 'unavailable':   nothing to offer yet (Chromium before the event fires).
 * - 'installed':     already running as the installed app, or installed just now.
 * - 'desktop':       laptops and desktop browsers: offline mode is mobile-only, so hidden.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { InstallPrompt } from '../services/offline/platform/installPrompt';
import { detectPlatform, isFirefox, isInAppBrowser, isStandalone } from '../services/offline/platform/deviceClass';

export type InstallMode = 'prompt' | 'ios' | 'firefox' | 'in-app' | 'unavailable' | 'installed' | 'desktop';

/** Pure decision, exported for tests. */
export function resolveInstallMode(input: {
  platform: ReturnType<typeof detectPlatform>;
  standalone: boolean;
  installed: boolean;
  canPrompt: boolean;
  inApp: boolean;
  firefox: boolean;
}): InstallMode {
  if (input.platform === 'desktop') return 'desktop';
  if (input.standalone || input.installed) return 'installed';
  if (input.inApp) return 'in-app';
  if (input.platform === 'ios' || input.platform === 'ipados') return 'ios';
  if (input.canPrompt) return 'prompt';
  if (input.firefox) return 'firefox';
  return 'unavailable';
}

const snapshot = () => resolveInstallMode({
  platform: detectPlatform(),
  standalone: isStandalone(),
  installed: InstallPrompt.wasInstalled(),
  canPrompt: InstallPrompt.canPrompt(),
  inApp: isInAppBrowser(),
  firefox: isFirefox(),
});

export function useInstallPrompt(): { mode: InstallMode; promptInstall: () => Promise<boolean> } {
  const mode = useSyncExternalStore((cb) => InstallPrompt.subscribe(cb), snapshot, () => 'desktop' as InstallMode);
  const promptInstall = useCallback(() => InstallPrompt.promptInstall(), []);
  return { mode, promptInstall };
}

export default useInstallPrompt;
